import * as React from 'react';
import { translate } from 'react-i18next';
import * as c from 'models/constants';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { IStore } from 'redux/IStore';
import { IAppointmentWithCustomer } from 'models/shop/appointments';
import { getOffice, getStaffMember } from '../../helpers';
import { Modal, WhiteButton, RedButton, ErrorSummary } from 'components';
import { ITranslateProps, InjectedFormProps } from 'models/common';
import { formatCurrency, formatDuration, submitForm } from 'helpers/common';
import { getCountryCallingCodeDomainValues } from 'redux/modules/domainvalues';
import { ButtonsSection } from 'components/PortalStructure';
import { Field, reduxForm } from 'redux-form';
import { BookingTextareaField } from 'components/FormControls';
import { setStatus } from 'redux/modules/shop/orders';
import { OrderPaymentStatus, OrderStatus } from 'models';
import Customer from 'components/Common/Customer';
import { maxLength } from 'helpers/validationattributes';
import renderIcon from 'helpers/renderIcon';
import { LoadingOverlayWrapper } from 'components/Loader';
import { getShopOrder } from 'redux/modules/shop/shopordercard';
import moment = require('moment');
import { momentWithTimezone } from 'redux/modules/shop/helpers';
import { InjectedManualBookingModalProps, ManualBookingScreen } from '..';
import { getAppointments, updateAppointmentsOnShopOrder } from 'redux/modules/shop/appointments';
import { ICancelAppointmentScreenFormData, IManualBookingFormData } from 'models/shop/manualbooking';
import { IOrderItem, IShopOrder } from 'models/orders/order';
import { CustomerCheckboxField } from 'containers/Shop/ShopBundlesServicesAddEdit/ShopServicesSingleAddEdit/CustomerCheckboxField';
require('./style.scss');

interface IOwnProps extends InjectedManualBookingModalProps {
	handleSubmit?: any;
	formErrors?: any;
	appointment: IAppointmentWithCustomer;
}

type IProps = IOwnProps &
	ReturnType<typeof stateToProps> &
	ReturnType<typeof dispatchToProps> &
	ITranslateProps &
	InjectedFormProps;

interface IState {
	isShowCancelAppointmentScreen: boolean;
	isSubmitting: boolean;
}

const maxLength750 = maxLength(750);

@translate(c.DEFAULT_TRANSLATION_NAMESPACE)
class CancelAppointmentComponent extends React.Component<IProps, IState> {
	constructor(props) {
		super(props);

		this.startCancel = this.startCancel.bind(this);

		this.state = {
			isShowCancelAppointmentScreen: false,
			isSubmitting: false,
		};

		this.cancelFormRef = React.createRef();
	}

	private cancelFormRef;

	public componentDidMount(): void {
		if (!this.props.countryCallingCodes) {
			this.props.getCountryCallingCodeDomainValues();
		}

		if (
			!this.props.shopOrderCard?.isFetching &&
			!this.props.shopOrderCard?.error &&
			this.props?.appointment &&
			this.props.shopOrderCard?.shopOrder?.id != this.props.appointment?.shopOrder?.id
		) {
			this.props.getShopOrder(this.props.appointment?.shopOrder?.id);
		} else if (this.props.shopOrderCard?.isFetching) {
			this.props.setScreen(ManualBookingScreen.Main);
		}

		if (this.props?.shopOrderCard?.shopOrder?.items?.[0]?.recurringBookingId) {
			this.props.change('deleteAllUpcomingItems', false);
		}
	}

	private startCancel(formData: ICancelAppointmentScreenFormData) {
		const { rejectShopOrder, updateAppointmentsOnShopOrderEdit, manualBookingFormValues, shopOrderCard } = this.props;
		this.setState({ isSubmitting: true });

		// if recurring event then update shop order if not removing all or cancel if all items are to be deleted
		if (shopOrderCard.shopOrder?.items?.[0]?.recurringBookingId) {
			let itemsToDelete = manualBookingFormValues.cancellationBookingItems.map((cbi) => cbi.productId);

			if (formData?.deleteAllUpcomingItems) {
				itemsToDelete = [...manualBookingFormValues.upcomingRecurringBookingItems.map((urbi) => urbi.productId)];
			}

			// if all items are to be deleted then simply cancel order
			if (itemsToDelete.length === shopOrderCard.shopOrder.items.length) {
				rejectShopOrder(
					this.props.shopOrderCard.shopOrder.id,
					this.props.shopOrderCard.shopOrder.shopId,
					formData?.messageForCustomer || '',
				)
					.then(() => {
						this.props.getAppointments().then(() => {
							this.props.closeModal();
						});
					})
					.catch(() => this.props.setScreen(ManualBookingScreen.Main));
			} else {
				// else remove only needed shop order items by productId
				updateAppointmentsOnShopOrderEdit(this.props.shopOrderCard.shopOrder, itemsToDelete)
					.then(() => {
						this.props.getAppointments().then(() => {
							this.props.closeModal();
						});
					})
					.catch(() => this.props.setScreen(ManualBookingScreen.Main));
			}
		} else {
			// if normal appointment then cancel order
			rejectShopOrder(
				this.props.shopOrderCard.shopOrder.id,
				this.props.shopOrderCard.shopOrder.shopId,
				formData?.messageForCustomer || '',
			)
				.then(() => {
					this.props.getAppointments().then(() => {
						this.props.closeModal();
					});
				})
				.catch(() => this.props.setScreen(ManualBookingScreen.Main));
		}
	}

	private renderCancelAppointmentScreen() {
		const { shopOrderCard, manualBookingFormValues } = this.props;
		const isRecurringEvent = shopOrderCard?.shopOrder?.items?.[0]?.recurringBookingId ? true : false;

		if (!shopOrderCard) {
			return null;
		}

		let shopOrderItems: IOrderItem[] = [];

		if (isRecurringEvent) {
			manualBookingFormValues.cancellationBookingItems.forEach((cbi) => {
				const currentItem = shopOrderCard.shopOrder.items.find((item) => item.productId === cbi.productId);
				if (currentItem) {
					shopOrderItems.push(currentItem);
				}
			});
		} else {
			shopOrderItems = shopOrderCard?.shopOrder?.items;
		}

		const orderAppointmentsElement = shopOrderItems?.map((item) => {
			if (item?.bookedOrderItemDatas?.length > 1) {
				// Bundle
				return (
					<div className="shopOrder-orderItem" key={item.productCode}>
						<p className="shopOrder-itemName">{item.productName}</p>
						{item?.bookedOrderItemDatas?.map((boid, idx) => {
							return (
								<div className="shopOrder-bundleItemNamesContainer" key={`${boid.orderItemId}${idx}`}>
									<p className="shopOrder-bundleItemName">{boid.bookingName}</p>
									<span className="shopOrder-resourceName">{boid.resourceName}</span>
								</div>
							);
						})}
						<div className="shopOrder-bundleItemInfo">
							<span className="shopOrder-duration">{formatDuration(item.productDuration)}</span>
							<span className="shopOrder-price">
								{formatCurrency(shopOrderCard.shopOrder.currencyKey, item.price || 0)}
							</span>
						</div>
					</div>
				);
			} else {
				// Single item
				const firstOrderItem = item?.bookedOrderItemDatas[0];

				return (
					<div className="shopOrder-orderItem" key={item.productCode}>
						<p className="shopOrder-itemName">{firstOrderItem.bookingName}</p>
						<div className="shopOrder-itemInfoContainer">
							<span className="shopOrder-resourceName">{firstOrderItem.resourceName}</span>
							<div>
								<span className="shopOrder-duration">{formatDuration(item.productDuration)}</span>
								<span className="shopOrder-price">
									{formatCurrency(shopOrderCard.shopOrder.currencyKey, item.price || 0)}
								</span>
							</div>
						</div>
					</div>
				);
			}
		});

		return orderAppointmentsElement;
	}

	private renderAppointmentTime() {
		const { shopOrderCard, manualBookingFormValues, t } = this.props;
		const isRecurringEvent = shopOrderCard?.shopOrder?.items?.[0]?.recurringBookingId ? true : false;

		if (!shopOrderCard) {
			return null;
		}

		let firstTime = '';
		let lastTime = '';
		let totalDuration = null;

		if (shopOrderCard?.shopOrder?.items.length) {
			if (isRecurringEvent && manualBookingFormValues?.cancellationBookingItems) {
				// get current items
				const currentShopOrderItems = [];
				manualBookingFormValues.cancellationBookingItems.forEach((cbi) => {
					const currentItem = shopOrderCard.shopOrder.items.find((item) => item.productId === cbi.productId);
					if (currentItem) {
						currentShopOrderItems.push(currentItem);
					}
				});

				currentShopOrderItems?.forEach((item, idxI) => {
					item.bookedOrderItemDatas.forEach((boid, idxJ) => {
						if (idxI === 0 && idxJ === 0) {
							firstTime = boid.startTime;
							lastTime = boid.endTime;
						}

						if (moment(firstTime).isAfter(moment(boid?.startTime))) {
							firstTime = boid.startTime;
						}

						if (moment(lastTime).isBefore(moment(boid?.endTime))) {
							lastTime = boid.endTime;
						}
					});
				});
			} else {
				shopOrderCard?.shopOrder.items.forEach((item, idxI) => {
					item.bookedOrderItemDatas.forEach((boid, idxJ) => {
						if (idxI === 0 && idxJ === 0) {
							firstTime = boid.startTime;
							lastTime = boid.endTime;
						}

						if (moment(firstTime).isAfter(moment(boid?.startTime))) {
							firstTime = boid.startTime;
						}

						if (moment(lastTime).isBefore(moment(boid?.endTime))) {
							lastTime = boid.endTime;
						}
					});
				});
			}
		}

		totalDuration = moment(lastTime).diff(moment(firstTime), 'seconds');
		const firstItem = isRecurringEvent
			? shopOrderCard?.shopOrder?.items.find(
					(i) => i.productId === manualBookingFormValues?.cancellationBookingItems?.[0]?.productId,
			  )
			: shopOrderCard?.shopOrder?.items[0];
		const timezone = firstItem?.officeZoneInfo;

		return (
			<div className="appointment-time">
				<div className="appointment-time__mainTime">{momentWithTimezone(timezone, firstTime).format('LLLL')}</div>
				<div className="appointment-time__subMainTime">
					{formatDuration(totalDuration)} {t('BookAppointment_Schedule_Duration')}{' '}
					{momentWithTimezone(timezone, lastTime).format('LT')}
				</div>
			</div>
		);
	}

	public render() {
		const { t, handleSubmit, shopOrderCard, shopCard, currentFormValues, manualBookingFormValues } = this.props;
		const isRecurringEvent = shopOrderCard?.shopOrder?.items?.[0]?.recurringBookingId ? true : false;
		let checkboxFieldText: string = undefined;

		if (!shopOrderCard) {
			return null;
		}

		let upcomingAppointmentsRemaining = 0;

		if (isRecurringEvent) {
			// Get number of appointments remaining in recurring event
			if (manualBookingFormValues?.upcomingRecurringBookingItems?.length) {
				const firstItem = manualBookingFormValues?.upcomingRecurringBookingItems?.[0] || null;
				upcomingAppointmentsRemaining =
					manualBookingFormValues?.upcomingRecurringBookingItems?.filter(
						(urbi) => urbi.bookingTemplateId === firstItem?.bookingTemplateId,
					)?.length - 1;
			}

			switch (upcomingAppointmentsRemaining) {
				case 1:
					checkboxFieldText = t('ManualBookingModal_CancelAppointment_RecurringToggle_1_Item', {
						number: upcomingAppointmentsRemaining,
					});
					break;
				case 2:
					checkboxFieldText = t('ManualBookingModal_CancelAppointment_RecurringToggle_2_Items', {
						number: upcomingAppointmentsRemaining,
					});
					break;
				case 3:
					checkboxFieldText = t('ManualBookingModal_CancelAppointment_RecurringToggle_3-4_Items', {
						number: upcomingAppointmentsRemaining,
					});
					break;
				case 4:
					checkboxFieldText = t('ManualBookingModal_CancelAppointment_RecurringToggle_3-4_Items', {
						number: upcomingAppointmentsRemaining,
					});
					break;
				default:
					checkboxFieldText = t('ManualBookingModal_CancelAppointment_RecurringToggleMultiple', {
						number: upcomingAppointmentsRemaining,
					});
			}
		}

		const footer = (
			<ButtonsSection align="justify">
				<WhiteButton
					onClick={() => {
						this.props.setScreen(ManualBookingScreen.Main);
					}}
				>
					{t('BSPortal_Common_Close')}
				</WhiteButton>
				<RedButton
					onClick={() => {
						submitForm(this.cancelFormRef.current);
					}}
					disabled={this.state?.isSubmitting}
				>
					<span className="cancelAppointmentScreen__desktopCancelButton">
						{shopOrderCard?.shopOrder?.order?.paymentStatus === OrderPaymentStatus.Verified
							? t('Shop_Appointments_Details_Cancel_Paid_Button')
							: t('Shop_Appointments_Details_Cancel_Button')}
					</span>
					<span className="cancelAppointmentScreen__mobileCancelButton">
						{shopOrderCard?.shopOrder?.order?.paymentStatus === OrderPaymentStatus.Verified
							? t('Shop_Appointments_Details_Cancel_Paid_Mobile_Button')
							: t('Shop_Appointments_Details_Cancel_Button')}
					</span>
				</RedButton>
			</ButtonsSection>
		);

		let orderStatusElement: JSX.Element = null;

		if (
			shopOrderCard?.shopOrder?.order?.paymentStatus === OrderPaymentStatus.Verified ||
			shopOrderCard?.shopOrder?.order?.message?.includes('Original paid order id')
		) {
			orderStatusElement = <span className="paid-online">{t('User_Order_PaidOnline')}</span>;
		} else if (shopOrderCard?.shopOrder?.order?.paymentStatus === OrderPaymentStatus.Rejected) {
			orderStatusElement = <span className="payment-rejected">{t('User_Order_PaymentRejected')}</span>;
		} else {
			orderStatusElement = <span className="payment-at-salon">{t('User_Order_PaymentAtSalon')}</span>;
		}

		return (
			<Modal
				footer={footer}
				title={
					isRecurringEvent
						? t('ManualBookingModal_CancelAppointment_RecurringTitle')
						: t('ManualBookingModal_CancelAppointment_Title')
				}
				size="medium"
				modalClass="modal-with-form cancelAppointmentScreen"
			>
				<LoadingOverlayWrapper
					active={
						this.state.isSubmitting || this.props.appointments?.isFetching || this.props.shopOrderCard?.isFetching
					}
					spinner={true}
					text={t('BSPortal_Common_Loading')}
				>
					<form onSubmit={handleSubmit(this.startCancel)} ref={this.cancelFormRef}>
						<div className="row">
							<div className="col-12">
								<p className="appointment-cancelDescription">
									{isRecurringEvent
										? t('ManualBookingModal_CancelAppointment_RecurringText')
										: t('Shop_Appointments_Cancel_Description')}
								</p>
							</div>
							{/** hide field if cancelling last recurring items */}
							{isRecurringEvent && upcomingAppointmentsRemaining > 0 && (
								<div className="col-12">
									<Field
										name={`deleteAllUpcomingItems`}
										component={CustomerCheckboxField}
										type="checkbox"
										label={checkboxFieldText}
										dontShowCustomer={true}
									/>
								</div>
							)}
							<div className="col-md-6 col-12">{this.renderAppointmentTime()}</div>
							<div className="col-md-6 col-12">
								<Customer customer={shopCard?.selectedCustomer} size="smallSize" />
							</div>
						</div>

						<div className="row">
							<div className="col-md-6 col-12">
								<h3 className="separator-title">{t('User_UserCard_PaymentStatus')}</h3>
								{orderStatusElement}
							</div>
							<div className="col-md-6 col-12">
								<h3 className="separator-title">{t('Orders_OrderCard_Label_OrderNumber')}</h3>
								<p>{shopOrderCard?.shopOrder?.id}</p>
							</div>
						</div>

						<div className="cancelAppointmentScreenContainer">
							<div
								className="cancelAppointmentScreenTitleContainer"
								onClick={() =>
									this.setState((prevState) => ({
										isShowCancelAppointmentScreen: !prevState.isShowCancelAppointmentScreen,
									}))
								}
							>
								<h3 className="cancelAppointmentScreen-title">{t('Shop_Appointments_AppointmentDetails')}</h3>
								{renderIcon(
									'arrow_left_round',
									18,
									18,
									`cancelAppointmentScreen-icon ${this.state.isShowCancelAppointmentScreen ? 'on' : 'off'}`,
								)}
							</div>
							{this.state.isShowCancelAppointmentScreen && shopOrderCard && this.renderCancelAppointmentScreen()}
						</div>

						{/** show cancel note field only if normal event or recurring event and cancelling entire order */}
						{!isRecurringEvent ||
						(isRecurringEvent &&
							currentFormValues?.deleteAllUpcomingItems &&
							manualBookingFormValues?.upcomingRecurringBookingItems?.length ===
								shopOrderCard?.shopOrder?.items?.length) ? (
							<>
								<h3 className="separator-title">{t('Shop_Appointments_Details_MessageForCustomer')}</h3>
								<Field
									name="messageForCustomer"
									component={BookingTextareaField}
									rows={2}
									type="text"
									validate={[maxLength750]}
								/>
							</>
						) : null}
						<ErrorSummary error={this.props.appointments.error} message={this.props.appointments.message} />
					</form>
				</LoadingOverlayWrapper>
			</Modal>
		);
	}
}

const stateToProps = (state: IStore, ownProps: IOwnProps) => {
	let currentFormValues: ICancelAppointmentScreenFormData = null;
	let manualBookingFormValues: IManualBookingFormData = null;

	if (state.form[c.FORM_NAME_CANCEL_APPOINTMENT] && state.form[c.FORM_NAME_CANCEL_APPOINTMENT].values) {
		currentFormValues = state.form[c.FORM_NAME_CANCEL_APPOINTMENT].values;
	}

	if (state.form[c.FORM_NAME_MANUAL_BOOKING_PLAN] && state.form[c.FORM_NAME_MANUAL_BOOKING_PLAN].values) {
		manualBookingFormValues = state.form[c.FORM_NAME_MANUAL_BOOKING_PLAN].values;
	}

	return {
		staffMembers: (ownProps.appointment?.orderItem?.bookedOrderItemDatas || []).map((bookedOrderItemData) =>
			getStaffMember(state, ownProps.appointment?.orderItem?.officeId, bookedOrderItemData?.resourceId),
		),
		office: getOffice(state, ownProps?.appointment?.orderItem?.officeId),
		countryCallingCodes: state.domainValues.countryCallingCodes,
		initialValues: {
			customerNote: (state.shopOrderCard?.shopOrder?.id == ownProps.appointment?.shopOrder?.id
				? state.shopOrderCard?.shopOrder
				: ownProps.appointment?.shopOrder
			)?.note,
		},
		currentFormValues,
		manualBookingFormValues,
		appointments: state.appointments,
		shopOrderCard: state.shopOrderCard,
		shopCard: state.shopcard,
	};
};

const dispatchToProps = (dispatch: Dispatch<IStore> /*, _ownProps: IOwnProps*/) => ({
	getCountryCallingCodeDomainValues: () => dispatch(getCountryCallingCodeDomainValues()),
	rejectShopOrder: (shopOrderId: string, shopId: string, note: string) =>
		dispatch(setStatus(shopOrderId, shopId, OrderStatus.Rejected, note)),
	updateAppointmentsOnShopOrderEdit: (shopOrder: IShopOrder, itemsToRemove: string[]) =>
		dispatch(updateAppointmentsOnShopOrder(shopOrder, undefined, itemsToRemove, undefined, false)),
	getShopOrder: (id: string) => dispatch(getShopOrder(id)),
	getAppointments: () => dispatch(getAppointments(undefined, true, undefined)),
});

export const CancelAppointmentScreen = connect(
	stateToProps,
	dispatchToProps,
)(
	reduxForm({
		form: c.FORM_NAME_CANCEL_APPOINTMENT,
		enableReinitialize: true,
	})(CancelAppointmentComponent),
);
